import React from 'react';
import { FormattedMessage } from 'react-intl';
import { ConsentType } from '@tink/config/src/tinkLinkConfig';
import { ProductType } from '@tink/core/src/types';
import { useConfiguration } from '../../../../../../ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { useProduct } from '../../../../../../ApplicationProviders/ProductProvider/ProductProvider';
import { messages } from './messages';
import { StyledLink } from './ThirdPartyLabel.styles';

type ThirdPartyLabelProps = {
  clientName: string;
  clientUrl: string;
  scopeModalTrigger: JSX.Element;
  isBusiness?: boolean;
};

export const ThirdPartyLabel: React.FC<ThirdPartyLabelProps> = ({
  clientName,
  clientUrl,
  scopeModalTrigger,
  isBusiness = false,
}) => {
  const { productType } = useProduct();
  const configuration = useConfiguration();

  const presentableClientName = clientUrl ? (
    <StyledLink target="_blank" rel="noopener noreferrer" href={clientUrl}>
      {clientName}
    </StyledLink>
  ) : (
    clientName
  );

  const consentForPersonal = (consentType: ConsentType) => {
    switch (consentType) {
      case ConsentType.FETCH:
        return (
          <FormattedMessage
            {...messages.clientWillObtainFinancialData}
            values={{
              clientName: presentableClientName,
            }}
          />
        );
      case ConsentType.FETCH_AND_DISPLAY:
        return <FormattedMessage {...messages.clientWillObtainAndDisplayFinancialData} />;
      default:
        return null;
    }
  };

  const consentForBusiness = () => {
    return (
      <FormattedMessage
        {...messages.businessClientWillObtainFinancialData}
        values={{
          clientName: presentableClientName,
        }}
      />
    );
  };

  const consentForAuthorize = () => {
    if (isBusiness) {
      return consentForBusiness();
    }

    return consentForPersonal(configuration.data.consentType);
  };

  const consentForAccountCheck = () => {
    const isClientNameAbbreviated = clientName.endsWith('.');

    return (
      <FormattedMessage
        {...messages.clientWillObtainAccountInformation}
        values={{ clientName, isAbbreviated: isClientNameAbbreviated }}
      />
    );
  };

  return (
    <>
      {productType === ProductType.ACCOUNT_CHECK ? consentForAccountCheck() : consentForAuthorize()}{' '}
      {scopeModalTrigger}
    </>
  );
};
